import { useMemo } from 'react';
import { FormattedDate, FormattedMessage, IntlShape, useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { GridColDef } from '@mui/x-data-grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import { Flex } from '@atoms/layout';
import { FaIcon } from '@atoms/icons';
import { ShiftStatusChip, TooltipNote } from '@atoms/dataDisplay';
import { AvatarList, ExternalIdLabel, isDateSeparator } from '@molecules/dataDisplay';
import { DateSeparator } from '@molecules/dataDisplay';
import { AssigneeColumn } from './assigneeColumn';
import { ShiftOld, ShiftStatus } from '@libs/models/shifts/shift';
import { Stack, Theme, Tooltip, useTheme } from '@mui/material';
import { ShiftType } from '@pages/schedule/components';

export function useShiftListColumns() {
  const theme = useTheme();
  const intl = useIntl();

  // Columns should be memoized to prevent table flickering and state loss
  return useMemo(() => {
    const columns: GridColDef<ShiftOld | DateSeparator>[] = [
      getShiftStartColumn(intl, 'shifts.fields.shiftType'),
      getExternalIdColumn(intl, 'shifts.listPage'),
      // getRecurrenceColumn(intl, 'shifts.fields.recurrence'),
      getQualificationsColumn(intl),
      getDepartmentColumn(intl),
      getSpecialtyColumn(intl),
      getStatusColumn(intl),
      getTagsColumn(intl, theme),
      getNotesColumn(intl),
      getShareColumn(intl),
      getAssigneeColumn(intl),
    ];
    return columns;
  }, [intl]);
}

function getExternalIdColumn(intl: IntlShape, localizationSubset: string): GridColDef<ShiftOld | DateSeparator> {
  return {
    field: 'externalId',
    headerName: intl.formatMessage({
      id: `${localizationSubset}.table.fields.externalId.heading`,
    }),
    width: 10,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }
      return (
        <ExternalIdLabel
          localizationSubset={`${localizationSubset}.table.fields.externalId`}
          externalId={params.value}
          mode={'hoverView'}
        />
      );
    },
  };
}

function getShiftStartColumn(
  intl: IntlShape,
  labelId: string,
): GridColDef<ShiftOld | DateSeparator, ShiftOld['shiftStart']> {
  return {
    field: 'shiftStart',
    headerName: intl.formatMessage({
      id: labelId,
    }),
    sortable: true,
    // We don't use unsorted list for the shift start date
    // because of shifts grouping by date
    sortingOrder: ['desc', 'asc'],
    sortComparator: (v1, v2, param1, param2) => {
      if (!v1) {
        return 0;
      }
      if (!v2) {
        return 1;
      }
      return `${v1.toISOString()}`.localeCompare(`${v1.toISOString()}`);
    },
    width: 160,
    colSpan: (_, row) => {
      if (isDateSeparator(row)) {
        return 8;
      }
      return 1;
    },
    renderCell: (params) => {
      if (isDateSeparator(params.row)) {
        return (
          <Flex alignCenter height="100%" px={0.75}>
            <Typography variant="subtitle1">
              <FormattedDate value={params.row.date} dateStyle="full" />
            </Typography>
          </Flex>
        );
      }

      return (
        <ShiftType
          startTime={dayjs(params.row.shiftStart).tz(params.row.timeZone)}
          endTime={dayjs(params.row.shiftEnd).tz(params.row.timeZone)}
          boxProps={{ height: '100%' }}
          isArchive={params.row.isArchive}
          isUrgent={params.row.isUrgent}
          isShared={params.row.isShared}
          isSeries={params.row.recurrence === 'series'}
          isRotation={params.row.recurrence === 'rotation'}
          isMultipleAssignees={!!params.row.canHasMultipleAssignee}
        />
      );
    },
  };
}

function getQualificationsColumn(intl: IntlShape): GridColDef<ShiftOld | DateSeparator, ShiftOld['qualifications']> {
  return {
    field: 'qualifications',
    headerName: intl.formatMessage({
      id: 'shifts.fields.qualifications',
    }),
    sortable: true,
    width: 140,
    sortComparator: (v1, v2) => {
      if (!v1) {
        return 0;
      }
      if (!v2) {
        return 1;
      }
      return `${v1[0].abbreviation}`.localeCompare(`${v2[0].abbreviation}`);
    },
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }

      return params.value.map((qual) => (
        <Tooltip key={qual.id} title={qual.name || ''}>
          <Chip variant="filled" label={qual.abbreviation} size="small" sx={{ mr: 1 }} />
        </Tooltip>
      ));
    },
  };
}

function getSpecialtyColumn(intl: IntlShape): GridColDef<ShiftOld | DateSeparator, ShiftOld['specialty']> {
  return {
    field: 'specialty',
    flex: 1,
    headerName: intl.formatMessage({
      id: 'shifts.fields.specialty',
    }),
    sortComparator: (v1, v2, param1, param2) => {
      if (!v1) {
        return 0;
      }
      if (!v2) {
        return 1;
      }
      return `${v1.name}`.localeCompare(`${v1.name}`);
    },
    width: 170,
    sortable: true,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }
      return <Chip variant="filled" label={params.value.name} size="small" />;
    },
  };
}

function getStatusColumn(intl: IntlShape): GridColDef<ShiftOld | DateSeparator, ShiftStatus> {
  return {
    field: 'status',
    headerName: intl.formatMessage({ id: 'shifts.fields.status' }),
    width: 120,
    sortable: true,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }

      return <ShiftStatusChip value={params.value} />;
    },
  };
}
function getTagsColumn(intl: IntlShape, theme: Theme): GridColDef<ShiftOld | DateSeparator, ShiftOld['tags']> {
  return {
    field: 'tags',
    headerName: intl.formatMessage({ id: 'shifts.listPage.table.fields.tags.heading' }),
    width: 70,
    renderCell: (params) => {
      const isEmpty = !Array.isArray(params.value) || !params.value.length || params.value?.every((tag) => !tag.trim());

      return (
        <TooltipNote
          title={
            <>
              {isEmpty && (
                <Typography variant="body2">
                  <FormattedMessage id="shifts.listPage.table.fields.tags.emptyTag" />
                </Typography>
              )}
              {!!params.value &&
                !!params.value.length &&
                params.value.map((tag, idx) => <Chip key={idx} label={tag.trim()} size="small" />)}
            </>
          }
          popoverTooltipSx={{
            px: isEmpty ? 2 : 1,
            color: isEmpty ? 'text.secondary' : 'inherit',
            maxWidth: '300px',
          }}
          placement="bottom-end"
        >
          {/* Span needed to be able to use Tooltip on disabled element */}
          <span>
            <IconButton
              size="small"
              disabled={isEmpty}
              sx={{ color: isEmpty ? theme.palette.text.secondary : theme.palette.primary.main }}
            >
              <FaIcon name="tags" color="inherit" />
            </IconButton>
          </span>
        </TooltipNote>
      );
    },
  };
}
function getNotesColumn(intl: IntlShape): GridColDef<ShiftOld | DateSeparator, ShiftOld['notes']> {
  return {
    field: 'notes',
    headerName: intl.formatMessage({ id: 'shifts.fields.notes' }),
    width: 50,
    renderCell: (params) => {
      const isEmpty = !Array.isArray(params.value) || !params.value.length || params.value?.every((tag) => !tag.trim());
      return (
        <TooltipNote
          title={
            <Typography variant="body2">
              {!isEmpty ? params.value : <FormattedMessage id="shifts.emptyNote" />}
            </Typography>
          }
          popoverTooltipSx={{
            px: 2.5,
            color: isEmpty ? 'text.secondary' : 'inherit',
          }}
          placement="bottom-end"
        >
          {/* Span needed to be able to use Tooltip on disabled element */}
          <span>
            <IconButton size="small" disabled={isEmpty}>
              <FaIcon name="memo-pad" color="inherit" />
            </IconButton>
          </span>
        </TooltipNote>
      );
    },
  };
}

function getShareColumn(intl: IntlShape): GridColDef<ShiftOld | DateSeparator, ShiftOld['agenciesForShare']> {
  return {
    field: 'agenciesForShare',
    headerName: intl.formatMessage({ id: 'shifts.fields.agenciesForShare' }),
    width: 50,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }
      return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', height: '100%' }}>
          <AvatarList
            list={params.value.map((v) => ({
              id: v.id,
              avatar: v.logoURL,
              name: v.name,
            }))}
          />
        </Stack>
      );
    },
  };
}

function getAssigneeColumn(intl: IntlShape): GridColDef<ShiftOld | DateSeparator, ShiftOld['assignee']> {
  return {
    field: 'assignee',
    headerName: intl.formatMessage({ id: 'shifts.fields.assignee' }),
    flex: 1,
    // To prevent assignee column component to collapse
    minWidth: 165,
    sortable: true,
    sortComparator: (v1, v2) => {
      if (!v1) {
        return 0;
      }
      if (!v2) {
        return 1;
      }
      return `${v1.name}`.localeCompare(`${v1.name}`);
    },
    renderCell: (params) => {
      if (!isDateSeparator(params.row)) {
        return <AssigneeColumn shift={params.row} />;
      }
    },
  };
}

function getDepartmentColumn(
  intl: IntlShape,
): GridColDef<ShiftOld | DateSeparator, ShiftOld['departmentNode']['name'] | null> {
  return {
    field: 'departmentNode',
    flex: 1,
    headerName: intl.formatMessage({
      id: 'shifts.fields.department',
    }),
    width: 170,
    valueGetter: (_, row) => {
      if ('departmentNode' in row) {
        return row.departmentNode.name;
      }
      return null;
    },
  };
}
